/**
 * Affiliate page (TMMPDM-0042): header, the status's actions, and one region per section.
 * Performance comes from the detail read. Agreement, referrals and codes each read themselves,
 * so one failure stays in its region. A hold is shown only when `reviewHold` is present.
 * Opening the page records a palette recent `{type:'affiliate', key:'<slug>/<id>'}`.
 * TMMPDM-0095: a Ledger tab (the partner ledger; `?tab=ledger` selects it). When the old
 * payout actions are retired (TMMPDM-0096) the review hold's Release hold… is hidden: the hold
 * alert stays, as history, without it.
 * TMMPDM-0100: one tab per section. The header and the two alerts stay above the tabs.
 */
import { useCallback, useEffect, useRef, useState } from 'react';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import { Alert, Box, Button, IconButton, Stack, Tooltip, Typography } from '@mui/material';
import { useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useCopy } from '@/hooks/useCopy';
import { oneOf, useUrlState, type UrlSchema } from '@/hooks/useUrlState';
import { FilterTabs } from '@/components/ui/FilterTabs';
import { useReturnTo } from '@/hooks/useReturnTo';
import { LoadingSpinner } from '@/components/LoadingSpinner';
import { PanelErrorBoundary } from '@/components/PanelErrorBoundary';
import { ErrorState, ObjectHeader, useNotify } from '@/components/page';
import type { HeaderAction } from '@/components/page/headerChrome';
import { useRecent } from '@/components/palette/useRecent';
import { ApiError } from '@/api/errors';
import { adminApi } from '@/api/tmm/admin';
import type { AffiliateDetail, AffiliateStatus } from '@/api/tmm/types';
import { formatDate } from '@/format';
import { AffiliateStatusChip } from './affiliateShared';
import { AffiliateEarningsSection } from './AffiliateEarningsSection';
import { AffiliatePerformanceTable } from './AffiliatePerformanceTable';
import { AffiliateAgreementSection } from './AffiliateAgreementSection';
import { AffiliateReferralsTable } from './AffiliateReferralsTable';
import { AffiliateCodesSection } from './AffiliateCodesSection';
import { AffiliateLedgerSection } from '../ledger/PartnerLedgerLeaves';
import { usePayoutActionsRetired } from '../ledger/ledger';
import {
  EndForCauseDialog,
  PauseAffiliateDialog,
  ReleaseHoldDialog,
  ResumeAffiliateDialog,
} from './AffiliateLifecycleDialogs';

const AFFILIATE_TABS = [
  'performance',
  'earnings',
  'ledger',
  'agreement',
  'referrals',
  'codes',
] as const;
type AffiliateTab = (typeof AFFILIATE_TABS)[number];
type AffiliateView = { tab: AffiliateTab };
const AFFILIATE_VIEW: UrlSchema<AffiliateView> = {
  tab: { default: 'performance', parse: oneOf(AFFILIATE_TABS) },
};

type PageError = 'affiliate-not-found' | 'generic' | null;

/**
 * The partner route resolves the tenant's slug before this page can read the affiliate. Its states
 * are this page's own, so the header (one `h1` element) is the same before and after the slug
 * arrives; `tenantSlug` is given once it has.
 */
export type AffiliateUpstream =
  | { state: 'pending' }
  | { state: 'missing' }
  | { state: 'failed'; failure: unknown; message: string; onRetry: () => void };

export function AffiliatePage({
  tenantSlug,
  upstream,
}: {
  tenantSlug?: string;
  upstream?: AffiliateUpstream;
}) {
  const { slug: routeSlug = '', affiliateId: affiliateIdParam = '' } = useParams();
  const slug = tenantSlug ?? routeSlug;
  const viaPartner = tenantSlug !== undefined || upstream !== undefined;
  return (
    <AffiliatePageContent
      key={`${viaPartner ? 'partner' : slug}/${affiliateIdParam}`}
      slug={slug}
      idParam={affiliateIdParam}
      upstream={upstream}
    />
  );
}

function AffiliatePageContent({
  slug,
  idParam,
  upstream,
}: {
  slug: string;
  idParam: string;
  upstream: AffiliateUpstream | undefined;
}) {
  const affiliateId = Number(idParam);
  const valid = slug.trim() !== '' && Number.isInteger(affiliateId) && affiliateId > 0;
  const { t } = useCopy();
  const { i18n } = useTranslation();
  const notify = useNotify();
  const { backTo } = useReturnTo();
  const { record: recordRecent, forget: forgetRecent } = useRecent();
  const [reloadToken, setReloadToken] = useState(0);
  const [affiliate, setAffiliate] = useState<AffiliateDetail | null>(null);
  const [tenantName, setTenantName] = useState<string | null>(null);
  const [resellerId, setResellerId] = useState<number | null>(null);
  const [currency, setCurrency] = useState<string | null>(null);
  const [loadedPlans, setLoadedPlans] = useState<{
    resellerId: number;
    names: ReadonlyMap<number, string>;
  } | null>(null);
  const planNames =
    loadedPlans !== null && loadedPlans.resellerId === resellerId ? loadedPlans.names : new Map();
  const [loading, setLoading] = useState(valid);
  const [loadError, setLoadError] = useState<PageError>(
    valid || upstream !== undefined ? null : 'affiliate-not-found',
  );
  const [loadFailure, setLoadFailure] = useState<unknown>(null);
  const [stateChanged, setStateChanged] = useState(false);
  const [dialog, setDialog] = useState<'pause' | 'resume' | 'end' | 'release' | null>(null);
  // TMMPDM-0096: the old review-hold release is retired with the other payout actions.
  const payoutActions = usePayoutActionsRetired();
  const [view, setView] = useUrlState(AFFILIATE_VIEW);

  const recentKey = `${slug}/${affiliateId}`;
  const recentGone = t('palette.recentGone', 'That record no longer exists.');
  const recentGoneRef = useRef(recentGone);
  useEffect(() => {
    recentGoneRef.current = recentGone;
  });

  useEffect(() => {
    if (!valid) return;
    let cancelled = false;
    Promise.all([adminApi.getAffiliate(slug, affiliateId), adminApi.getTenant(slug)])
      .then(([detail, tenant]) => {
        if (cancelled) return;
        setAffiliate(detail);
        setTenantName(tenant.reseller ? (tenant.reseller.name ?? `#${tenant.reseller.id}`) : '—');
        setResellerId(tenant.reseller?.id ?? null);
        setCurrency(tenant.currency);
        setLoadError(null);
        recordRecent({ type: 'affiliate', key: recentKey, label: detail.displayName });
      })
      .catch((error: unknown) => {
        if (cancelled) return;
        setLoadFailure(error);
        const notFound = error instanceof ApiError && error.status === 404;
        setLoadError(notFound ? 'affiliate-not-found' : 'generic');
        if (notFound && forgetRecent('affiliate', recentKey)) {
          notify.info(recentGoneRef.current);
        }
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [slug, affiliateId, valid, reloadToken, recentKey, recordRecent, forgetRecent, notify]);

  useEffect(() => {
    if (resellerId === null) return;
    let cancelled = false;
    const id = resellerId;
    adminApi
      .listCommissionPlans(id)
      .then((list) => {
        if (cancelled) return;
        setLoadedPlans({
          resellerId: id,
          names: new Map(list.commissionPlans.map((plan) => [plan.id, plan.name])),
        });
      })
      .catch(() => {
        if (!cancelled) setLoadedPlans({ resellerId: id, names: new Map() });
      });
    return () => {
      cancelled = true;
    };
  }, [resellerId]);

  const reload = useCallback(() => {
    setLoading(true);
    setReloadToken((n) => n + 1);
  }, []);

  const applyDetail = (detail: AffiliateDetail) => {
    setAffiliate(detail);
    setDialog(null);
    setStateChanged(false);
  };

  const onStateChanged = () => {
    setDialog(null);
    setStateChanged(true);
    reload();
  };

  const partnerPath =
    resellerId === null
      ? `/partners/by-tenant/${encodeURIComponent(slug)}`
      : `/partners/${resellerId}`;
  const tenantsCrumb = { label: t('page.partners.title', 'Partners'), to: backTo('/partners') };

  // One header for every state (TMM-B7): loading, not found, failed and loaded are the same
  // `ObjectHeader` element, so the `h1` is one element whose text changes — never replaced — and
  // the late title is announced once.
  const affiliatesTitle = t('tenant.tab.affiliates', 'Affiliates');
  if (upstream?.state === 'failed') {
    return (
      <Box>
        <ObjectHeader
          breadcrumbs={[tenantsCrumb, { label: affiliatesTitle }]}
          name={affiliatesTitle}
        />
        <ErrorState
          error={upstream.failure}
          message={upstream.message}
          onRetry={upstream.onRetry}
        />
      </Box>
    );
  }

  if (upstream?.state === 'missing') {
    const title = t('partners.notFound', 'Partner not found');
    return (
      <Box>
        <ObjectHeader breadcrumbs={[tenantsCrumb, { label: title }]} name={title} />
      </Box>
    );
  }

  // Not yet answered: the partner is being resolved, or the affiliate read is in flight.
  if (affiliate === null && (loading || upstream?.state === 'pending' || loadError === null)) {
    return (
      <Box>
        <ObjectHeader
          breadcrumbs={[tenantsCrumb, { label: affiliatesTitle }]}
          name={affiliatesTitle}
        />
        <LoadingSpinner />
      </Box>
    );
  }

  if (loadError === 'affiliate-not-found' || !valid) {
    const title = t('affiliates.page.notFound', 'Affiliate not found');
    return (
      <Box>
        <ObjectHeader breadcrumbs={[tenantsCrumb, { label: title }]} name={title} />
      </Box>
    );
  }

  if (loadError === 'generic' || affiliate === null || tenantName === null || currency === null) {
    return (
      <Box>
        {/* The slug is an identifier: mono, in the subtitle line. The h1 stays sans (TMM-T1). */}
        <ObjectHeader
          breadcrumbs={[tenantsCrumb, { label: slug }]}
          name={t('tenant.tab.affiliates', 'Affiliates')}
          meta={
            <Typography
              variant="body1"
              color="text.secondary"
              sx={(theme) => theme.typography.mono}
            >
              {slug}
            </Typography>
          }
        />
        <ErrorState
          error={loadFailure}
          message={t('affiliates.page.loadError', 'Could not load this affiliate.')}
          onRetry={reload}
        />
      </Box>
    );
  }

  const locale = i18n.language;
  const absent = t('affiliates.absent', '—');
  const dateOf = (value: string | undefined) =>
    value !== undefined ? formatDate(value, locale) : absent;
  const copyIdLabel = t('affiliates.page.copyId', 'Copy ID {{id}}', { id: affiliate.id });
  const copyId = async () => {
    try {
      await navigator.clipboard.writeText(String(affiliate.id));
      notify.success(t('components.copied', 'Copied'));
    } catch {
      notify.info(t('components.copyFailed', "Couldn't copy — select the text instead."));
    }
  };

  return (
    <Box>
      <ObjectHeader
        breadcrumbs={[
          tenantsCrumb,
          { label: tenantName, to: partnerPath },
          {
            label: t('tenant.tab.affiliates', 'Affiliates'),
            to: `${partnerPath}?tab=affiliates`,
          },
          { label: affiliate.displayName },
        ]}
        name={affiliate.displayName}
        identifier={affiliate.email}
        secondaryIdentifier={String(affiliate.id)}
        status={
          <Stack direction="row" spacing={0.5} sx={{ alignItems: 'center' }}>
            <AffiliateStatusChip status={affiliate.status} />
            <Tooltip title={copyIdLabel}>
              <IconButton size="small" aria-label={copyIdLabel} onClick={() => void copyId()}>
                <ContentCopyIcon sx={{ fontSize: 16 }} />
              </IconButton>
            </Tooltip>
          </Stack>
        }
        {...actionsFor(affiliate.status, t, setDialog)}
      />
      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
        {t('affiliates.dates.applied', 'Applied {{date}}', { date: dateOf(affiliate.appliedAt) })}
        {' · '}
        {t('affiliates.dates.approved', 'Approved {{date}}', {
          date: dateOf(affiliate.approvedAt),
        })}
        {' · '}
        {t('affiliates.dates.ended', 'Ended {{date}}', { date: dateOf(affiliate.endedAt) })}
      </Typography>
      {stateChanged ? (
        <Alert severity="warning" sx={{ mb: 2 }} onClose={() => setStateChanged(false)}>
          {t('affiliates.stateChanged', "This affiliate's status changed")}
        </Alert>
      ) : null}
      {affiliate.reviewHold !== undefined ? (
        <Alert
          severity="warning"
          sx={{ mb: 2 }}
          action={
            payoutActions.retired ? undefined : (
              <Button color="inherit" size="small" onClick={() => setDialog('release')}>
                {t('affiliates.release.action', 'Release hold…')}
              </Button>
            )
          }
        >
          {t(
            'affiliates.hold.alert',
            'Payouts held for review since {{date}}: {{reason}} (by {{by}})',
            {
              date: formatDate(affiliate.reviewHold.since, locale),
              reason: affiliate.reviewHold.reason,
              by: affiliate.reviewHold.by,
            },
          )}
        </Alert>
      ) : null}
      <FilterTabs
        ariaLabel={t('affiliates.tabs.aria', 'Affiliate sections')}
        active={view.tab}
        onChange={(next) => {
          const tab = oneOf(AFFILIATE_TABS)(next);
          if (tab) setView({ tab });
        }}
        tabs={[
          { key: 'performance', label: t('affiliates.performance.title', 'Performance') },
          { key: 'earnings', label: t('affiliates.earnings.title', 'Earnings') },
          { key: 'ledger', label: t('ledger.title', 'Ledger') },
          { key: 'agreement', label: t('affiliates.agreement.title', 'Agreement') },
          { key: 'referrals', label: t('affiliates.referrals.title', 'Referrals') },
          { key: 'codes', label: t('affiliates.codes.title', 'Codes') },
        ]}
      />
      <Box sx={{ mt: 2 }}>
        <PanelErrorBoundary key={view.tab}>
          {view.tab === 'performance' ? (
            <AffiliatePerformanceTable
              performance={affiliate.performance}
              referrals={affiliate.referrals}
              weeklySignups={affiliate.weeklySignups}
            />
          ) : null}
          {view.tab === 'earnings' ? (
            <AffiliateEarningsSection slug={slug} affiliateId={affiliate.id} />
          ) : null}
          {view.tab === 'ledger' ? (
            <AffiliateLedgerSection
              slug={slug}
              affiliateId={affiliate.id}
              name={affiliate.displayName}
            />
          ) : null}
          {view.tab === 'agreement' ? (
            <AffiliateAgreementSection
              slug={slug}
              affiliateId={affiliate.id}
              resellerId={resellerId}
              currency={currency}
            />
          ) : null}
          {view.tab === 'referrals' ? (
            <AffiliateReferralsTable slug={slug} affiliateId={affiliate.id} planNames={planNames} />
          ) : null}
          {view.tab === 'codes' ? (
            <AffiliateCodesSection slug={slug} affiliateId={affiliate.id} />
          ) : null}
        </PanelErrorBoundary>
      </Box>

      <PauseAffiliateDialog
        open={dialog === 'pause'}
        slug={slug}
        affiliateId={affiliate.id}
        name={affiliate.displayName}
        onClose={() => setDialog(null)}
        onDone={applyDetail}
        onStateChanged={onStateChanged}
      />
      <ResumeAffiliateDialog
        open={dialog === 'resume'}
        slug={slug}
        affiliateId={affiliate.id}
        name={affiliate.displayName}
        onClose={() => setDialog(null)}
        onDone={applyDetail}
        onStateChanged={onStateChanged}
      />
      <EndForCauseDialog
        open={dialog === 'end'}
        slug={slug}
        affiliateId={affiliate.id}
        name={affiliate.displayName}
        onClose={() => setDialog(null)}
        onDone={applyDetail}
        onStateChanged={onStateChanged}
      />
      <ReleaseHoldDialog
        open={dialog === 'release'}
        slug={slug}
        affiliateId={affiliate.id}
        name={affiliate.displayName}
        ledgerTo="?tab=ledger"
        onMovedToLedger={payoutActions.reload}
        onClose={() => setDialog(null)}
        onReleased={() => {
          setDialog(null);
          notify.success(
            t('affiliates.release.done', 'Hold released. Unpaid statements can be paid.'),
          );
          reload();
        }}
      />
    </Box>
  );
}

function actionsFor(
  status: AffiliateStatus,
  t: (key: string, fallback?: string) => string,
  setDialog: (dialog: 'pause' | 'resume' | 'end' | 'release') => void,
): { primaryAction?: HeaderAction; secondaryActions?: HeaderAction[] } {
  const end: HeaderAction = {
    label: t('affiliates.end.action', 'End for cause…'),
    onClick: () => setDialog('end'),
  };
  if (status === 'ACTIVE') {
    return {
      primaryAction: end,
      secondaryActions: [
        { label: t('affiliates.pause.action', 'Pause'), onClick: () => setDialog('pause') },
      ],
    };
  }
  if (status === 'PAUSED') {
    return {
      primaryAction: end,
      secondaryActions: [
        { label: t('affiliates.resume.action', 'Resume'), onClick: () => setDialog('resume') },
      ],
    };
  }
  return {};
}
